/**
 *  @swagger
 *  definitions:
 *    RegattaProtestParams:
 *      type: object
 *      properties:
 *        RegattaId:
 *          description: ID of the associated Regatta
 *          type: string
 *        CaseNo:
 *          description: Case No of the RegattaProtest
 *          type: string
 *        RaceDate:
 *          description: Race Date of the RegattaProtest
 *          type: string
 *          format: date
 *        RaceNo:
 *          description: Race No of the RegattaProtest
 *          type: string
 *        Type:
 *          description: Type of the RegattaProtest
 *          $ref: '#/components/enums/RegattaProtestType'
 *        Rule:
 *          description: Rule of the RegattaProtest
 *          type: string
 *        Protestor:
 *          description: Protestor of the RegattaProtest
 *          type: string
 *        ProtestorGroup:
 *          description: Protestor Group of the RegattaProtest
 *          type: string
 *        ProtestorRegattaEntryId:
 *          description: ID of the associated RegattaEntry
 *          type: string
 *        Protestee:
 *          description: Protestee of the RegattaProtest
 *          type: string
 *        ProtesteeGroup:
 *          description: Protestee Group of the RegattaProtest
 *          type: string
 *        ProtesteeRegattaEntryId:
 *          description: ID of the associated RegattaEntry
 *          type: string
 *        Witnesses:
 *          description: Witnesses of the RegattaProtest
 *          type: string
 *        Hailing:
 *          description: Hailing of the RegattaProtest
 *          type: boolean
 *        HailingContent:
 *          description: HailingContent of the RegattaProtest
 *          type: string
 *        Flag:
 *          description: Flag of the RegattaProtest
 *          type: boolean
 *        FlagContent:
 *          description: FlagContent of the RegattaProtest
 *          type: string
 *        AltInfo:
 *          description: AltInfo of the RegattaProtest
 *          type: boolean
 *        AltInfoContent:
 *          description: AltInfoContent of the RegattaProtest
 *          type: string
 *        PreContent:
 *          description: PreContent of the RegattaProtest
 *          type: string
 *        Content:
 *          description: Content of the RegattaProtest
 *          type: string
 *        Drawing:
 *          description: Drawing of the RegattaProtest
 *          type: string
 *        HearingDate:
 *          description: Hearing Date of the RegattaProtest
 *          type: string
 *          format: date-time
 *        Decision:
 *          description: Decision of the RegattaProtest
 *          type: string
 *        File:
 *          description: File of the RegattaProtest
 *          type: string
 *        Image:
 *          description: Image of the RegattaProtest
 *          type: string
 *
 *  @swagger
 *  definitions:
 *    RegattaProtestCreateParams:
 *      allOf:
 *        - $ref: '#/definitions/RegattaProtestParams'
 *        - type: object
 *          required:
 *            - RegattaId
 *            - RaceDate
 *            - Type
 *            - Protestor
 *            - Protestee
 *            - Hailing
 *            - Flag
 *            - AltInfo
 *
 *  @swagger
 *  definitions:
 *    RegattaProtestQueryParams:
 *      allOf:
 *        - type: object
 *          properties:
 *            id:
 *              description: ID of the RegattaProtest
 *              type: string
 *        - $ref: '#/definitions/RegattaProtestParams'
 *
 *  @swagger
 *  definitions:
 *    RegattaProtestUpdateParams:
 *      allOf:
 *        - $ref: '#/definitions/RegattaProtestQueryParams'
 *        - type: object
 *          required:
 *            - id
 *
 *  @swagger
 *  definitions:
 *    RegattaProtest:
 *      allOf:
 *        - $ref: '#/definitions/RegattaProtestCreateParams'
 *        - $ref: '#/definitions/RegattaProtestUpdateParams'
 *        - $ref: '#/definitions/Timestamps'
 *
 *  @swagger
 *  components:
 *    schemas:
 *      RegattaProtest:
 *        allOf:
 *          - $ref: '#/definitions/RegattaProtest'
 *          - type: object
 *            properties:
 *              Regatta:
 *                description: Regatta of the RegattaProtest
 *                $ref: '#/components/schemas/Regatta'
 *              ProtestorRegattaEntry:
 *                description: ProtestorRegattaEntry of the RegattaProtest
 *                $ref: '#/components/schemas/RegattaEntry'
 *              ProtesteeRegattaEntry:
 *                description: ProtesteeRegattaEntry of the RegattaProtest
 *                $ref: '#/components/schemas/RegattaEntry'
 */

// Enums
import {
  RegattaProtestType,
} from '../enums';

// Types
import type {
  $Model,
  $ModelClass,
  $ModelClasses,
} from '.';
import type {
  $Field,
} from '../types';

export type $RelationIds = {
  ProtesteeRegattaEntryId: null | string;
  ProtestorRegattaEntryId: null | string;
  RegattaId: string;
};

export type $Params = {
  AltInfo: boolean;
  AltInfoContent: null | string;
  CaseNo: null | string;
  Content: null | string;
  Decision: null | string;
  Drawing: null | string;
  File: null | string;
  Flag: boolean;
  FlagContent: null | string;
  Hailing: boolean;
  HailingContent: null | string;
  HearingDate?: Date | null;
  Image: null | string;
  PreContent: null | string;
  Protestee: string;
  ProtesteeGroup: null | string;
  Protestor: string;
  ProtestorGroup: null | string;
  RaceDate: string;
  RaceNo: null | string;
  Rule: null | string;
  Type: RegattaProtestType;
  Witnesses: null | string;
};

export type $CreateParams = $Params & $RelationIds;

export type $UpdateParams = {
  id: string;
} & Partial<$CreateParams>;

export type $UpsertParams = $CreateParams & {
  id?: string;
};

export type $QueryParams = {
  id?: null | string;
} & Partial<$CreateParams>;

export type $Associations = {
  ProtesteeRegattaEntry?: $Model<'RegattaEntry'>;
  ProtestorRegattaEntry?: $Model<'RegattaEntry'>;
  Regatta?: $Model<'Regatta'>;
};

export type $ModelParams = $Associations & $CreateParams & {
  id: string;
};

type $Fields = $Params & {
  id: string;
};

const fields = (
  Sequelize,
): Record<keyof $Fields, $Field> => ({
  AltInfo: {
    allowNull: false,
    defaultValue: false,
    type: Sequelize.BOOLEAN,
  },
  AltInfoContent: {
    allowNull: true,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  CaseNo: {
    allowNull: true,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  Content: {
    allowNull: true,
    type: Sequelize.TEXT,
    validate: {
      notEmpty: true,
    },
  },
  Decision: {
    allowNull: true,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  Drawing: {
    allowNull: true,
    type: Sequelize.TEXT,
    validate: {
      notEmpty: true,
    },
  },
  File: {
    allowNull: true,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  Flag: {
    allowNull: false,
    defaultValue: false,
    type: Sequelize.BOOLEAN,
  },
  FlagContent: {
    allowNull: true,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  Hailing: {
    allowNull: false,
    defaultValue: false,
    type: Sequelize.BOOLEAN,
  },
  HailingContent: {
    allowNull: true,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  HearingDate: {
    allowNull: true,
    type: Sequelize.DATE,
    validate: {
      notEmpty: true,
    },
  },
  id: {
    allowNull: false,
    defaultValue: Sequelize.UUIDV4,
    primaryKey: true,
    type: Sequelize.UUID,
    unique: true,
  },
  Image: {
    allowNull: true,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  PreContent: {
    allowNull: true,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  Protestee: {
    allowNull: false,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  ProtesteeGroup: {
    allowNull: true,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  Protestor: {
    allowNull: false,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  ProtestorGroup: {
    allowNull: true,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  RaceDate: {
    allowNull: false,
    type: Sequelize.DATEONLY,
    validate: {
      notEmpty: true,
    },
  },
  RaceNo: {
    allowNull: true,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  Rule: {
    allowNull: true,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  Type: {
    allowNull: false,
    type: Sequelize.ENUM(...Object.values(RegattaProtestType)),
    validate: {
      notEmpty: true,
    },
  },
  Witnesses: {
    allowNull: true,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
});

export default ((
  Sequelize,
  stack,
): $ModelClass<'RegattaProtest'> => {
  const RegattaProtest = stack.define(
    'RegattaProtest',
    fields(Sequelize),
    {
      tableName: 'RegattaProtests',
    },
  );

  RegattaProtest.associate = (models: $ModelClasses) => {
    models.RegattaProtest.belongsTo(
      models.Regatta,
      {
        as: 'Regatta',
        foreignKey: 'RegattaId',
      },
    );
    models.RegattaProtest.belongsTo(
      models.RegattaEntry,
      {
        as: 'ProtestorRegattaEntry',
        constraints: false,
        foreignKey: 'ProtestorRegattaEntryId',
      },
    );
    models.RegattaProtest.belongsTo(
      models.RegattaEntry,
      {
        as: 'ProtesteeRegattaEntry',
        constraints: false,
        foreignKey: 'ProtesteeRegattaEntryId',
      },
    );
  };

  RegattaProtest.addScope(
    'defaultScope',
    {
      order: [['createdAt', 'ASC']],
    },
  );

  return RegattaProtest;
});
